import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  DataTable,
  Page,
  Image,
  Loading,
  Card,
  Text,
  Spinner,
  ButtonGroup,
  Button,
  Modal,
  Toast,
  FormLayout,
  Select,
  Box,
  Collapsible,
  Icon,
  Divider,
  BlockStack,
  InlineStack,
  Avatar,
  RangeSlider,
  Thumbnail,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import emptyListImage from "../../Assets/Images/emptyList.svg";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  EditIcon,
  CategoriesIcon,
  PlusCircleIcon,
} from "@shopify/polaris-icons";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import parse from "html-react-parser";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

const BrandBenchmark = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [openCard, setOpenCard] = useState({});
  const [countryButtons, setCountryButtons] = useState([]);
  const [country, setCountry] = useState("All");
  const [optionsCategories, setOptionsCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [toastActive, setToastActive] = useState(false);
  const [categoryButtons, setCategoryButtons] = useState([]);

  const toggleToast = useCallback(() => setToastActive((prev) => !prev), []);

  /** Fetch countries */
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "" },
          { label: "Mutual", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
            flag: country.flag,
          })),
        ];
        setCountryButtons(options);
      })
      .catch((err) => console.log(err));
  }, []);

  /** Fetch brand benchmark items with filters */
  const fetchApps = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== "All") {
        params.append("filter[category_id]", categoryFilter);
      }
      if (country) {
        params.append("filter[competitor_country]", country);
      }

      const response = await axios.get("/brand-benchmarks", { params });
      const data = response?.data?.data || [];
      console.log("data=", data);

      setItems(data);
    } catch (error) {
      console.error("Error fetching apps:", error);
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, country]);
  // ---------------------------
  // 1️⃣ Fetch categories
  // ---------------------------
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true); // show spinner immediately
        const response = await axios.get(`/brand-benchmark-categories`);
        const data = response?.data?.data?.data || [];
        const options = data.map((item) => ({
          label: item.title,
          value: String(item.id),
        }));

        setCategoryButtons(options);

        if (options.length > 0) {
          // set default category and trigger fetchApps later
          setCategoryFilter((prev) => prev || String(options[0].value));
          localStorage.setItem("benchmark-category", String(options[0].value));
        } else {
          // ❗ no categories → stop spinner after load finishes
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategoryButtons([]);
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // ---------------------------
  // 2️⃣ Fetch apps when ready
  // ---------------------------
  const didMount = useRef(false);

  useEffect(() => {
    // if still loading categories or no filter yet, skip
    if (!categoryFilter) return;
    if (categoryButtons.length === 0) return; // no categories

    if (!didMount.current) didMount.current = true;

    fetchApps();
  }, [categoryFilter, country, categoryButtons]);

  /** Toggle collapsible cards */
  const toggleCard = (id) => {
    setOpenCard((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /** Scroll handler for sliders */
  const scroll = (category, direction) => {
    const slider = document.getElementById(`slider-${category}`);
    if (slider) {
      slider.scrollBy({
        left: direction === "next" ? 300 : -300,
        behavior: "smooth",
      });
    }
  };

  /** Group apps by category for slider layout */
  const groupedApps = items?.reduce((acc, app) => {
    const category = app.criteria?.title || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(app);
    return acc;
  }, {});
  const [currentItem, setCurrentItem] = useState(null);
  const [zoom, setZoom] = useState(50);
  const [imageIndexes, setImageIndexes] = useState({});
  const handlePrevImage = (appId, screenshotsLength) => {
    setImageIndexes((prev) => {
      const currentIndex = prev[appId] ?? 0; // ✅ default to 0
      return {
        ...prev,
        [appId]: currentIndex > 0 ? currentIndex - 1 : screenshotsLength - 1,
      };
    });
  };

  const handleNextImage = (appId, screenshotsLength) => {
    setImageIndexes((prev) => {
      const currentIndex = prev[appId] ?? 0; // ✅ default to 0
      return {
        ...prev,
        [appId]: currentIndex < screenshotsLength - 1 ? currentIndex + 1 : 0,
      };
    });
  };
  useEffect(() => {
    if (items) {
      const initialIndexes = Object.fromEntries(
        items.map((app) => [app.id, 0])
      );
      setImageIndexes(initialIndexes);
    }
  }, [items]);

  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    localStorage.removeItem("benchmark-category");
  }, []);

  return (
    <Page
      title="Screenshots"
      secondaryActions={
        <ButtonGroup>
          <Button
            onClick={() => navigate("/admin/brand-benchmark/edit/categories")}
            icon={CategoriesIcon}
          >
            Categories
          </Button>
        </ButtonGroup>
      }
      fullWidth
      backAction={{
        onAction: () => navigate(`/admin/competitors`),
      }}
    >
      <FormLayout>
        {/* Filters */}

        <Card>
          <BlockStack gap="500">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center", // 👈 centers both sides vertically
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              {/* 🔹 Left side: Filters */}
              <InlineStack gap="300" wrap align="center">
                <ButtonGroup segmented>
                  {categoryButtons.map((t) => (
                    <Button
                      key={t.value}
                      pressed={categoryFilter === t.value}
                      onClick={() => {
                        if (categoryFilter !== t.value) {
                          localStorage.setItem(
                            "benchmark-category",
                            String(t.value)
                          );
                          setCategoryFilter(String(t.value));
                        }
                      }}
                    >
                      {t.label}
                    </Button>
                  ))}
                </ButtonGroup>
                <div style={{ maxWidth: 300 }} className="ms-4">
                  <RangeSlider
                    label="Zoom"
                    min={0}
                    max={100}
                    step={5}
                    value={zoom}
                    onChange={setZoom}
                    output
                  />
                </div>
              </InlineStack>

              {/* 🔹 Right side: Country buttons (centered vertically) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center", // 👈 vertical centering
                }}
              >
                <ButtonGroup segmented>
                  {countryButtons.map((t) => (
                    <Button
                      key={t.value}
                      pressed={country === t.value}
                      onClick={() => setCountry(String(t.value))}
                    >
                      {t.flag && (
                        <img
                          src={t.flag}
                          alt={t.flag}
                          style={{ width: 20, marginRight: 6 }}
                        />
                      )}
                      {t.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </div>
          </BlockStack>
        </Card>

        {/* App listings */}
        <Card background="bg-surface-secondary">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            <Text variant="headingLg" as="h2"></Text>
            <Button
              variant="primary"
              onClick={() => navigate("/admin/brand-benchmark/new")}
              icon={PlusCircleIcon}
            >
              Add Item
            </Button>
          </div>
          <div>
            {isLoading ? (
              <div className="spinnerContainer">
                <div className="vertical-center">
                  <Spinner size="large" />
                </div>
              </div>
            ) : items.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "25%" }}>
                  <img src={emptyListImage} alt="Empty list" />
                </div>
                <Text variant="headingLg" as="h5" tone="subdued">
                  No results found
                </Text>
              </div>
            ) : (
              Object.entries(groupedApps).map(([category, appsInCategory]) => (
                <div
                  key={category}
                  style={{
                    position: "relative",
                    width: "100%",
                    // marginTop: `${zoom * 3}px`,
                    marginBottom: `${zoom * 3.5}px`,

                    transition: "margin 0.3s ease",
                  }}
                >
                  {/* Category title (not affected by zoom) */}
                  <div style={{ paddingLeft: "32px" }}>
                    <Text variant="headingLg" as="h2">
                      {category}
                    </Text>
                  </div>

                  {/* Category slider wrapper */}
                  <div style={{ position: "relative", width: "100%" }}>
                    {/* ⬅️ Previous button */}
                    <button
                      onClick={() => scroll(category, "prev")}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        background: "rgba(255,255,255,0.7)",
                        border: "none",
                        borderRadius: "50%",
                        padding: "6px",
                        cursor: "pointer",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      }}
                    >
                      <Icon source={ArrowLeftIcon} />
                    </button>

                    {/* ➡️ Next button */}
                    <button
                      onClick={() => scroll(category, "next")}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        background: "rgba(255,255,255,0.7)",
                        border: "none",
                        borderRadius: "50%",
                        padding: "6px",
                        cursor: "pointer",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      }}
                    >
                      <Icon source={ArrowRightIcon} />
                    </button>

                    {/* 🔹 Slider section - zoom applied here only */}
                    <div
                      style={{
                        width: "100%",
                        overflow: "visible",
                        transformOrigin: "top left",
                        transition: "all 0.3s ease",
                        // Adjust actual layout height proportionally
                        height: `${400 * (zoom / 50)}px`, // base card height × zoom factor
                        marginBottom: `${zoom}px`, // dynamic spacing between categories
                      }}
                    >
                      <div
                        id={`slider-${category}`}
                        style={{
                          overflowX: "auto",
                          scrollBehavior: "smooth",
                          padding: "16px 32px",
                        }}
                      >
                        {/* inner group that zooms together */}
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            transform: `scale(${zoom / 50})`, // ✅ all cards zoom together
                            transformOrigin: "top left", // ✅ keeps them aligned to the left
                            transition: "transform 0.3s ease",
                            width: "max-content", // ✅ prevents clipping on zoom-out
                          }}
                        >
                          {appsInCategory.map((app) => (
                            <div
                              key={app.id}
                              style={{
                                flex: "0 0 auto",
                                cursor: "pointer",
                                display: "inline-flex", // allow the width to shrink/grow with image
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                              // onClick={() => {
                              //   setCurrentItem(app.screenshots);
                              //   setIsOpen(true);
                              // }}
                              onClick={() => {
                                // ✅ Collect all screenshots of this category
                                const allScreenshots = appsInCategory
                                  .flatMap((a) => a.screenshots || [])
                                  .filter(Boolean);

                                setCurrentItem(allScreenshots);
                                setStartIndex(
                                  allScreenshots.findIndex(
                                    (s) =>
                                      s ===
                                      app.screenshots?.[
                                        imageIndexes[app.id] || 0
                                      ]
                                  )
                                ); // start from clicked image
                                setIsOpen(true);
                              }}
                            >
                              {/* 👇 Wrapper controls the card size */}
                              <div

                              // style={{
                              //   width: "fit-content", // 👈 makes the card fit image width
                              //   minWidth: "200px", // 👈 optional: don’t let too-narrow images collapse
                              //   maxWidth: "600px", // 👈 optional: safety cap for very wide images
                              // }}
                              >
                                <Card padding={0}>
                                  <div
                                    onMouseEnter={() => setHoveredCard(app.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    style={{
                                      position: "relative",
                                      height: "400px",
                                      display: "inline-flex", // ✅ lets width expand naturally
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#f5f5f5",
                                      overflow: "visible", // ✅ allow full width (no clipping)
                                      padding: "0", // ensure no inner offset
                                    }}
                                  >
                                    <img
                                      src={
                                        app.screenshots?.[
                                          imageIndexes[app.id] || 0
                                        ] || ""
                                      }
                                      alt=""
                                      style={{
                                        height: "100%", // ✅ always fill full height
                                        width: "auto", // ✅ natural width based on aspect ratio
                                        maxWidth: "none", // ✅ prevents parent constraints
                                        objectFit: "contain", // ✅ never crop
                                        display: "block",
                                      }}
                                    />
                                    {/* your nav + edit buttons here */}
                                    {hoveredCard === app.id &&
                                      app.screenshots?.length > 1 && (
                                        <>
                                          <div
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePrevImage(
                                                app.id,
                                                app.screenshots?.length || 0
                                              );
                                            }}
                                            style={{
                                              position: "absolute",
                                              left: "10px",
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              zIndex: 10,
                                              width: "36px",
                                              height: "36px",
                                              borderRadius: "50%",
                                              backgroundColor:
                                                "rgba(255,255,255,0.85)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <Icon source={ArrowLeftIcon} />
                                          </div>

                                          <div
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleNextImage(
                                                app.id,
                                                app.screenshots?.length || 0
                                              );
                                            }}
                                            style={{
                                              position: "absolute",
                                              right: "10px",
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              zIndex: 10,
                                              width: "36px",
                                              height: "36px",
                                              borderRadius: "50%",
                                              backgroundColor:
                                                "rgba(255,255,255,0.85)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <Icon source={ArrowRightIcon} />
                                          </div>
                                        </>
                                      )}
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "8px",
                                        right: "8px",
                                        zIndex: 20,
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/admin/brand-benchmark/${app.id}`
                                        );
                                      }}
                                    >
                                      <div
                                        style={{
                                          background: "rgba(255,255,255,0.85)",
                                          borderRadius: "50%",
                                          width: "32px",
                                          height: "32px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                          // transition: "background 0.2s ease",
                                        }}
                                        // onMouseEnter={(e) =>
                                        //   (e.currentTarget.style.background =
                                        //     "rgba(0,0,0,0.8)")
                                        // }
                                        // onMouseLeave={(e) =>
                                        //   (e.currentTarget.style.background =
                                        //     "rgba(0,0,0,0.6)")
                                        // }
                                      >
                                        <Icon source={EditIcon} color="base" />
                                      </div>
                                    </div>
                                  </div>

                                  <Box
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCard(app.id);
                                    }}
                                    padding="400"
                                    background="bg-surface-disabled"
                                    // style={{ backgroundColor: "red" }}
                                    borderRadius="0 0 8px 8px"
                                    border="border"
                                    // style={{ backgroundColor: "red" }}
                                    // backgroundColor="red"
                                  >
                                    <InlineStack
                                      align="space-between"
                                      blockAlign="center"
                                    >
                                      <InlineStack align="center" gap="200">
                                        <Thumbnail
                                          size="small"
                                          alt={app.competitor?.business_name}
                                          source={app.competitor?.logo || ""}
                                        />
                                        <Text variant="headingMd" as="h2">
                                          {app.competitor?.business_name}
                                        </Text>
                                      </InlineStack>

                                      <Button
                                        size="large"
                                        variant="plain"
                                        icon={
                                          openCard[app.id]
                                            ? ArrowUpIcon
                                            : ArrowDownIcon
                                        }
                                      />
                                    </InlineStack>

                                    <Collapsible open={openCard[app.id]}>
                                      <Box paddingBlockStart="400">
                                        {app.description && (
                                          <div>{parse(app.description)}</div>
                                        )}
                                      </Box>
                                    </Collapsible>
                                  </Box>
                                </Card>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
        {currentItem?.length && (
          <Lightbox
            open={isOpen}
            close={() => setIsOpen(false)}
            index={startIndex} // ✅ show clicked image first
            slides={currentItem.map((item) => ({
              src: item || "",
            }))}
            zoom={{
              scrollToZoom: true,
              maxZoomPixelRatio: 5,
              minZoom: 1,
              zoomInMultiplier: 2,
            }}
            plugins={[Zoom]}
          />
        )}
      </FormLayout>

      {toastActive && (
        <Toast content="Filters updated successfully" onDismiss={toggleToast} />
      )}
    </Page>
  );
};

export default BrandBenchmark;
